/**
 * The Settled tab's rows: the session's socket receipts, plus the archive.
 *
 * **The tab was in-session only by design and that design has expired.** §F24f
 * built it on `StoreSet.settlements` because the channel was already subscribed
 * and cost a render rather than an endpoint — the point being that a dismissed
 * toast must not be the only place a user could learn they won. That argument
 * covers *this* session. It does not cover a reload, and a market lasts sixty
 * seconds.
 *
 * So: one page of `GET /portfolio/settlements` on mount, more on request, merged
 * with the live rows and deduped on `settlement_id`. The two sources overlap by
 * construction — anything that settled while this tab was open is in both — and
 * `settlement_id` is what makes that harmless.
 *
 * **This is not a poll.** One request when the tab is opened and one per
 * explicit "Load more"; nothing on a timer. `settlements` has a channel, and
 * invariant 8 is about timers, not about ever fetching. `docs/03` Appendix B
 * still lists three, and this adds none.
 *
 * **`version`, not `rows`, is the dependency** (invariant 17). `LogStore.append`
 * does `entries.unshift(...)`, mutating in place, so a `useMemo` keyed on the
 * array alone runs once on mount with an empty list and never again — which is
 * exactly how the settlement toast came to never fire (§F25k).
 */

import { useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useRuntime, useSettlements } from '@/app/use-runtime'

export interface SettlementRow extends Record<string, unknown> {
  settlement_id?: string
  settled_at_ms?: number
}

interface Page {
  data?: SettlementRow[]
  next_cursor?: string | null
  has_more?: boolean
}

export interface SettlementArchive {
  rows: readonly SettlementRow[]
  /** True while the first page is in flight and the store has nothing to show. */
  loading: boolean
  hasMore: boolean
  loadingMore: boolean
  loadMore: () => void
  /** The archive fetch failed. The live rows still render; the tab is not blank. */
  failed: boolean
}

export function useSettlementArchive(enabled: boolean): SettlementArchive {
  const runtime = useRuntime()
  const { rows: live, version } = useSettlements<SettlementRow>()

  const query = useInfiniteQuery({
    queryKey: ['portfolio', 'settlements'],
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      runtime.api.portfolio.listSettlements(
        pageParam ? { cursor: pageParam, limit: 50 } : { limit: 50 },
      ) as Promise<Page>,
    // `next_cursor` is `null` **exactly when** `has_more` is false
    // (`openapi.CursorPage`), so reading one field is enough — and reading the
    // cursor is the one that cannot loop: a truthy `has_more` with no cursor
    // would page forever on the same request.
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    // A settled market is immutable. Re-fetching the archive because a component
    // remounted would be a request that can only return what it returned before.
    staleTime: 5 * 60_000,
  })

  const archive = query.data?.pages ?? []

  const rows = useMemo(() => {
    const byId = new Map<string, SettlementRow>()
    // Live first, so a socket receipt wins over an archive row for the same
    // settlement. They should be identical; if they are not, the one that
    // arrived on the authoritative channel is the one to show.
    for (const row of [...live, ...archive.flatMap((p) => p.data ?? [])]) {
      const id = typeof row.settlement_id === 'string' ? row.settlement_id : null
      if (id === null || byId.has(id)) continue
      byId.set(id, row)
    }
    return [...byId.values()].sort((a, b) => (b.settled_at_ms ?? 0) - (a.settled_at_ms ?? 0))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, version, archive])

  return {
    rows,
    loading: enabled && query.isPending && live.length === 0,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    failed: query.isError,
  }
}
